import { useCallback } from 'react';
import { Box, Button, Stack, Switch, Typography } from '@mui/material';

import { HURDLR_SERIF, SectionHeading } from './fields';
import { PAYMENT_METHOD_LABELS } from './format';
import InvoiceTotals from './InvoiceTotals';
import { PaymentMethod } from './types';

import MailIcon from 'modules/common/components/Icons/MailIcon';

export type SaveAction = 'send' | 'final' | 'link' | 'draft' | 'quote';

const SECONDARY_ACTIONS: { action: SaveAction; label: string }[] = [
    { action: 'final', label: 'Save Final Without Sending' },
    { action: 'link', label: 'Save Final and Create Link' },
    { action: 'draft', label: 'Save as Draft' },
    { action: 'quote', label: 'Send as Quote' },
];

const PAYMENT_METHODS = Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[];

interface InvoiceSendPanelProps {
    number: string;
    subtotal: number;
    discountLabel: string;
    total: number;
    paymentMethods: PaymentMethod[];
    onPaymentMethodsChange: (methods: PaymentMethod[]) => void;
    onSave: (action: SaveAction) => void;
}

interface PaymentMethodToggleProps {
    method: PaymentMethod;
    checked: boolean;
    onToggle: (method: PaymentMethod, checked: boolean) => void;
}

function PaymentMethodToggle({ method, checked, onToggle }: Readonly<PaymentMethodToggleProps>) {
    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ py: 1, borderBottom: 1, borderColor: 'cement.300' }}>
            <Typography variant="body2">{PAYMENT_METHOD_LABELS[method]}</Typography>
            <Switch
                variant="primary"
                checked={checked}
                onChange={(e) => onToggle(method, e.target.checked)}
                inputProps={{ 'aria-label': PAYMENT_METHOD_LABELS[method] }}
            />
        </Stack>
    );
}

export default function InvoiceSendPanel({
    number,
    subtotal,
    discountLabel,
    total,
    paymentMethods,
    onPaymentMethodsChange,
    onSave,
}: Readonly<InvoiceSendPanelProps>) {
    const handleToggle = useCallback(
        (method: PaymentMethod, checked: boolean) =>
            onPaymentMethodsChange(
                checked ? [...paymentMethods, method] : paymentMethods.filter((existing) => existing !== method)
            ),
        [paymentMethods, onPaymentMethodsChange]
    );
    const handleSend = useCallback(() => onSave('send'), [onSave]);

    return (
        <>
            <Typography variant="h3" sx={{ fontFamily: HURDLR_SERIF, fontWeight: 400, mb: 2 }}>
                Invoice {number}
            </Typography>
            <InvoiceTotals subtotal={subtotal} discountLabel={discountLabel} total={total} />

            <Box sx={{ mt: 6 }}>
                <SectionHeading>Payment Processing</SectionHeading>
                {PAYMENT_METHODS.map((method) => (
                    <PaymentMethodToggle
                        key={method}
                        method={method}
                        checked={paymentMethods.includes(method)}
                        onToggle={handleToggle}
                    />
                ))}
            </Box>

            <Box sx={{ mt: 8 }}>
                <SectionHeading>Save and Send</SectionHeading>
                <Stack alignItems="flex-start" spacing={0.5} sx={{ mb: 2 }}>
                    {SECONDARY_ACTIONS.map(({ action, label }) => (
                        <Button
                            key={action}
                            variant="text"
                            color="primary"
                            size="small"
                            sx={{ px: 0, minWidth: 0 }}
                            onClick={() => onSave(action)}>
                            {label}
                        </Button>
                    ))}
                </Stack>
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    endIcon={<MailIcon fontSize="small" />}
                    disabled={paymentMethods.length === 0}
                    onClick={handleSend}>
                    Save and Send
                </Button>
            </Box>
        </>
    );
}
